/**
 * AdminLoginPage — dedicated admin-only sign-in portal at /admin-login.
 *
 * Deliberately separate from the consumer /login page (which only exposes
 * Buyer/Seller tabs). Email + password only: no Google OAuth, no mobile
 * OTP — admin accounts are always email/password. If the credentials
 * belong to a non-admin user, the session is torn down immediately and
 * an error is shown.
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { Shield, Eye, EyeOff, Mail, Lock } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_live-commerce-hub-4/artifacts/29xleaxd_ChatGPT%20Image%20May%2014%2C%202026%2C%2012_57_41%20PM.png";

export default function AdminLoginPage() {
  const { login, logout, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in as an admin → go straight to the console.
  // (useEffect — calling nav() during render warns under StrictMode.)
  useEffect(() => {
    if (user?.role === "admin") nav("/admin");
  }, [user, nav]);
  if (user?.role === "admin") return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password, true);
      if (u.role !== "admin") {
        // Non-admin credential — tear the session down immediately.
        await logout();
        setErr("This account does not have admin access.");
        return;
      }
      nav("/admin");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] px-4 py-6 max-w-md mx-auto flex flex-col">
      <div className="mb-4">
        <Link to="/" className="text-xs font-bold uppercase tracking-widest text-[#A1A1AA] hover:text-[#00F0FF]"
          data-testid="admin-login-back-btn">← Back</Link>
      </div>

      <div className="flex flex-col items-center text-center mt-6 mb-7">
        <img src={LOGO} alt="ShopRizz" className="h-20 sm:h-24 w-auto mb-5 drop-shadow-[0_0_30px_rgba(0,240,255,0.25)]"/>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-3">
          <Shield size={12} className="text-[#00F0FF]"/>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#00F0FF]">Restricted access</span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase leading-[0.95]">
          admin <span className="text-[#00F0FF]">portal</span>.
        </h1>
        <p className="text-[#A1A1AA] text-sm mt-3 max-w-xs">Platform operations, moderation and control.</p>
      </div>

      <div className="card p-5 grain relative overflow-hidden" data-testid="admin-login-card">
        <form onSubmit={onSubmit} className="space-y-3" data-testid="admin-login-form">
          <div className="relative">
            <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
            <input data-testid="admin-login-email" type="email" required placeholder="Admin email"
              className="input-dark pl-10" value={email}
              onChange={e => setEmail(e.target.value)} autoFocus/>
          </div>
          <div className="relative">
            <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
            <input data-testid="admin-login-password"
              type={showPw ? "text" : "password"} required placeholder="Password"
              className="input-dark pl-10 pr-12" value={password}
              onChange={e => setPassword(e.target.value)}/>
            <button type="button" onClick={() => setShowPw(!showPw)}
              data-testid="admin-login-toggle-pw"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#00F0FF] p-1"
              aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          {err && (
            <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2"
              data-testid="admin-login-error">{err}</div>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full py-3 rounded-full font-display font-bold uppercase tracking-widest text-sm text-black bg-[#00F0FF] hover:bg-[#D4FF00] active:scale-[0.98] transition disabled:opacity-50">
            {busy ? "Verifying…" : "Sign in to console"}
          </button>
        </form>
        <p className="text-[10px] text-center text-[#52525B] mt-4">
          Admins only. Buyer or seller?{" "}
          <Link to="/login" className="text-[#A1A1AA] hover:text-[#D4FF00] underline" data-testid="admin-login-consumer-link">
            Use the main sign-in
          </Link>
        </p>
      </div>
    </div>
  );
}
